import { Typography } from '@mui/material';
import { CreateLLMModelSpecificationRequest, ModelType } from 'api/model';
import { getSchema, getUiSchema } from 'api/model-specifications/formSchema';
import useModelSpecifications from 'api/model-specifications/useModelSpecifications';
import FormBlock from 'components/form/FormBlock';
import FormButtons from 'components/form/FormButtons';
import { SettingsFormShell } from 'components/settingsHub/SettingsFormShell';
import { useMemo, useState } from 'react';
import SpecificationSourceLink from './SpecificationSourceLink';

const FORM_ID = 'model-spec-add-form';

type Props = {
  onClose: () => void;
};

const ModelSpecificationAdd = ({ onClose }: Props) => {
  const [provider, setProvider] = useState<ModelType | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const { modelSpecifications, create } = useModelSpecifications(provider, provider !== undefined);

  const providerGroups = useMemo(
    () => Array.from(new Set(modelSpecifications?.map((spec) => spec.group).filter((group) => group))).sort(),
    [modelSpecifications],
  );

  const handleSubmit = async (formData: CreateLLMModelSpecificationRequest) => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      await create.mutateAsync(formData);
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (formData: CreateLLMModelSpecificationRequest) => {
    if (formData.provider !== provider) {
      setProvider(formData.provider);
    }
  };

  return (
    <SettingsFormShell
      title="Add Model Specification"
      footer={<FormButtons formId={FORM_ID} onCancel={onClose} cancelBtnTxt="back to list" isLoading={isLoading} />}
    >
      <Typography variant="body2" color="textSecondary" className="mb-4">
        Define a new LLM model specification to be used in your projects.
        <br />
        Specification is taken from the <SpecificationSourceLink />.
      </Typography>
      <FormBlock<CreateLLMModelSpecificationRequest>
        isLoading={isLoading}
        formId={FORM_ID}
        showButtons={false}
        schema={getSchema()}
        uiSchema={getUiSchema(providerGroups)}
        onSubmit={(formData) => void handleSubmit(formData)}
        onChange={handleChange}
        onCancel={onClose}
      ></FormBlock>
    </SettingsFormShell>
  );
};

export default ModelSpecificationAdd;
